import { useCallback, useEffect, useState } from 'react';
import { api } from './api';
import { isIos, isStandalone } from './pwa';

/**
 * Web Push on this device (opt in, per device). The server only sends a short "agent X is done"
 * notification; the service worker (public/sw.js) shows it. Subscribing always follows a click:
 * browsers (and iOS) require the permission prompt to come from a user gesture.
 */

const flagKey = (userId: string) => `nivio-chat:push:${userId}`;
const SYNCED_KEY = 'nivio-chat:push-synced';

function readFlag(userId: string): boolean {
  try {
    return window.localStorage.getItem(flagKey(userId)) === '1';
  } catch {
    return false;
  }
}

function writeFlag(userId: string, on: boolean): void {
  try {
    if (on) window.localStorage.setItem(flagKey(userId), '1');
    else window.localStorage.removeItem(flagKey(userId));
  } catch {
    // Blocked storage: the browser's own subscription still decides.
  }
}

export function pushApiSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** The registration of our worker, without waiting for one that will never come (dev, blocked). */
async function registration(): Promise<ServiceWorkerRegistration | null> {
  return (await navigator.serviceWorker.getRegistration('/')) ?? null;
}

function keyBytes(base64Url: string): Uint8Array<ArrayBuffer> {
  const padded = base64Url
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(base64Url.length / 4) * 4, '=');
  const raw = atob(padded);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

async function register(
  reg: ServiceWorkerRegistration,
  publicKey: string,
): Promise<PushSubscription> {
  const options = { userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) };
  try {
    return await reg.pushManager.subscribe(options);
  } catch (err) {
    // A subscription made with another server key blocks a new one: replace it once.
    const old = await reg.pushManager.getSubscription();
    if (!old) throw err;
    await old.unsubscribe();
    return reg.pushManager.subscribe(options);
  }
}

async function sendToServer(sub: PushSubscription): Promise<void> {
  const json = sub.toJSON();
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  if (!json.endpoint || !p256dh || !auth) throw new Error('incomplete push subscription');
  await api.pushSubscribe({
    endpoint: json.endpoint,
    keys: { p256dh, auth },
    userAgent: navigator.userAgent.slice(0, 200),
  });
}

export type PushState =
  | 'loading'
  | 'unsupported' // browser or worker cannot do push
  | 'server-off' // not configured on the server
  | 'ios-install' // iOS only delivers push to an installed app
  | 'denied'
  | 'off'
  | 'on';

export function usePushSettings(userId: string): {
  state: PushState;
  busy: boolean;
  failed: boolean;
  enable: () => Promise<void>;
  disable: () => Promise<void>;
} {
  const [state, setState] = useState<PushState>('loading');
  const [config, setConfig] = useState<{ enabled: boolean; publicKey?: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const refresh = useCallback(async (cfg: { enabled: boolean } | null) => {
    if (!pushApiSupported()) return setState('unsupported');
    if (isIos() && !isStandalone()) return setState('ios-install');
    const reg = await registration();
    if (!reg) return setState('unsupported');
    if (!cfg?.enabled) return setState('server-off');
    if (Notification.permission === 'denied') return setState('denied');
    const sub = await reg.pushManager.getSubscription();
    setState(sub && Notification.permission === 'granted' ? 'on' : 'off');
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      let cfg: { enabled: boolean; publicKey?: string } | null = null;
      try {
        cfg = await api.pushConfig();
      } catch {
        cfg = null;
      }
      if (!alive) return;
      setConfig(cfg);
      await refresh(cfg).catch(() => alive && setState('unsupported'));
    })();
    return () => {
      alive = false;
    };
  }, [refresh]);

  const enable = useCallback(async () => {
    if (!config?.enabled || !config.publicKey) return;
    setBusy(true);
    setFailed(false);
    try {
      // First call after the click: the permission prompt needs the user gesture.
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return;
      const reg = await registration();
      if (!reg) throw new Error('no service worker');
      const sub = await register(reg, config.publicKey);
      try {
        await sendToServer(sub);
      } catch (err) {
        await sub.unsubscribe().catch(() => undefined);
        throw err;
      }
      writeFlag(userId, true);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
      await refresh(config).catch(() => undefined);
    }
  }, [config, refresh, userId]);

  const disable = useCallback(async () => {
    setBusy(true);
    setFailed(false);
    try {
      await unsubscribeThisDevice(userId);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
      await refresh(config).catch(() => undefined);
    }
  }, [config, refresh, userId]);

  return { state, busy, failed, enable, disable };
}

/**
 * Removes this device's subscription on the server and in the browser. Used by "turn off" and
 * before signing out, so a shared phone never keeps notifying the previous person.
 */
export async function unsubscribeThisDevice(userId?: string): Promise<void> {
  if (userId) writeFlag(userId, false);
  if (!pushApiSupported()) return;
  const reg = await registration();
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return;
  try {
    await api.pushUnsubscribe(sub.endpoint);
  } finally {
    await sub.unsubscribe().catch(() => undefined);
  }
}

/**
 * On sign-in or app start: if this person turned notifications on here and the browser still
 * allows them, make sure the browser has a subscription and the server knows its current
 * endpoint (browsers rotate them). Best effort; once per tab session per endpoint.
 */
export async function syncPushSubscription(userId: string): Promise<void> {
  if (!pushApiSupported() || !readFlag(userId) || Notification.permission !== 'granted') return;
  if (isIos() && !isStandalone()) return;
  const reg = await registration();
  if (!reg) return;
  const config = await api.pushConfig();
  if (!config.enabled || !config.publicKey) return;
  const sub = (await reg.pushManager.getSubscription()) ?? (await register(reg, config.publicKey));
  try {
    if (window.sessionStorage.getItem(SYNCED_KEY) === sub.endpoint) return;
  } catch {
    // Blocked storage: sync again.
  }
  await sendToServer(sub);
  try {
    window.sessionStorage.setItem(SYNCED_KEY, sub.endpoint);
  } catch {
    // Blocked storage.
  }
}

/**
 * Whether Nivi's brief and monitor pushes reach this device. Only offered while this device
 * has a subscription the server knows; approvals and finished runs always come through.
 */
export function useThisDeviceNiviPushes(): {
  available: boolean;
  on: boolean;
  busy: boolean;
  set: (on: boolean) => Promise<void>;
} {
  const [endpoint, setEndpoint] = useState<string | null>(null);
  const [on, setOn] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!pushApiSupported() || Notification.permission !== 'granted') return;
      const sub = await (await registration())?.pushManager.getSubscription();
      if (!sub) return;
      const device = await api.pushDevice(sub.endpoint);
      if (!alive) return;
      setOn(device.niviPushes);
      setEndpoint(sub.endpoint);
    })().catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  const set = useCallback(
    async (next: boolean) => {
      if (!endpoint) return;
      setBusy(true);
      setOn(next);
      try {
        setOn((await api.pushDevice(endpoint, next)).niviPushes);
      } catch {
        setOn(!next);
      } finally {
        setBusy(false);
      }
    },
    [endpoint],
  );

  return { available: endpoint !== null, on, busy, set };
}
